import * as React from 'react';

import { OrderedMap } from 'immutable';
import { connect, Dispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import UploadButton, { Props as UploadButtonProps } from 'components/UploadButton';
import {
  State as FileUploadsState,
  loadThumbnail,
  uploadRequest,
  UploadRequestData,
  UploadRequestGroup,
} from 'reducers/fileUploads';

/**
 * ## Wraps UploadButton with
 * ### State managed in redux reducers/fileUploads.js
 *
 * ```example
 * <ReduxUploadButton>
 *  Click here to upload file
 * </ReduxUploadButton>
 * ```
 */

type ReduxStateProps = { fileUploads: UploadRequestGroup };

export type ReduxDispatchProps = {
  uploadRequest: typeof uploadRequest;
  loadThumbnail?: typeof loadThumbnail;
};

export class ReduxUploadButton extends React.Component<
  UploadButtonProps & ReduxStateProps & ReduxDispatchProps
> {
  render() {
    const { children, fileContextKey, fileUploads, privateUpload, ...rest } = this.props;
    return (
      <UploadButton
        fileContextKey={fileContextKey}
        uploadRequest={this.props.uploadRequest}
        // any items in this file upload context means we shouldn't have more attachments!
        disabled={fileUploads.size > 0}
        privateUpload={privateUpload}
        {...rest}>
        {children}
      </UploadButton>
    );
  }
}

const mapStateToProps = (
  initialState: { fileUploads?: FileUploadsState },
  ownProps: UploadButtonProps
): ReduxStateProps => {
  const { fileUploads } = initialState;

  // this is done so the render function never has to worry about the correct file context for the component!
  const fileGroup = fileUploads && fileUploads.get(ownProps.fileContextKey);
  return {
    fileUploads: fileGroup ?? OrderedMap<string, UploadRequestData>(),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps =>
  bindActionCreators(
    {
      uploadRequest,
      loadThumbnail,
    },
    dispatch
  );

export default connect<ReduxStateProps, ReduxDispatchProps, UploadButtonProps>(
  mapStateToProps,
  mapDispatchToProps
)(ReduxUploadButton);
